import React, {
    useEffect,
    useRef,
    useState,
} from "react";

import api from "../../api/api";

import {
    Row,
    Col,
    Card,
    Typography,
    Form,
    Input,
    Select,
    Button,
    Avatar,
    Upload,
    Space,
    Tag,
    Divider,
} from "antd";

import {
    UserAddOutlined,
    UploadOutlined,
    EyeOutlined,
    EyeInvisibleOutlined,
    UserOutlined,
    MailOutlined,
    LockOutlined,
    CopyOutlined,
    SafetyCertificateOutlined,
    CameraOutlined,
    CheckCircleFilled,
    IdcardOutlined,
    TeamOutlined,
    KeyOutlined,
} from "@ant-design/icons";

import defaultAvatar from "./studentava.jpg";
import CartoonAlertPopup from "./CartoonAlertPopup";
import {
    downloadParentAuthenticationPDF,
} from "./ParentAuthenticationPDF";

const { Title, Text } = Typography;
const { Option } = Select;

const AddStudentsStaff = () => {
    const [form] = Form.useForm();

    const watchedUsername = Form.useWatch(
        "username",
        form
    );

    const watchedEmail = Form.useWatch(
        "email",
        form
    );

    const watchedPassword = Form.useWatch(
        "password",
        form
    );

    const [imagePreview, setImagePreview] =
        useState(null);

    const [imageFile, setImageFile] =
        useState(null);

    const [loading, setLoading] =
        useState(false);

    const [checkingCredentials, setCheckingCredentials] =
        useState(false);

    const [emailGenerated, setEmailGenerated] =
        useState(false);

    const [passwordGenerated, setPasswordGenerated] =
        useState(false);

    const [createdCredentials, setCreatedCredentials] =
        useState(null);

    const [popup, setPopup] = useState({
        show: false,
        type: "",
        message: "",
    });

    const emailGeneratedRef = useRef(false);
    const passwordGeneratedRef = useRef(false);

    const requestIdRef = useRef(0);
    const debounceRef = useRef(null);
    const imageObjectUrlRef = useRef(null);

    /*
     * Keep the credentials returned by the suggestion
     * endpoint separate from credentials typed by the admin.
     *
     * This prevents a late username request from changing
     * the password after the administrator has already edited it.
     */
    const generatedCredentialsRef = useRef({
        email: "",
        password: "",
    });

    useEffect(() => {
        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }

            if (imageObjectUrlRef.current) {
                URL.revokeObjectURL(
                    imageObjectUrlRef.current
                );
            }
        };
    }, []);

    const closePopup = () => {
        setPopup({
            show: false,
            type: "",
            message: "",
        });
    };

    const clearGeneratedCredentials = () => {
        generatedCredentialsRef.current = {
            email: "",
            password: "",
        };

        if (emailGeneratedRef.current) {
            form.setFieldValue("email", "");
        }

        if (passwordGeneratedRef.current) {
            form.setFieldValue("password", "");
            form.setFieldValue(
                "confirm_password",
                ""
            );
        }

        emailGeneratedRef.current = false;
        passwordGeneratedRef.current = false;

        setEmailGenerated(false);
        setPasswordGenerated(false);
    };

    const handleFormValuesChange = (
        changedValues
    ) => {
        /*
         * If the administrator manually edits email,
         * it is no longer a generated credential.
         */
        if (
            Object.prototype.hasOwnProperty.call(
                changedValues,
                "email"
            )
        ) {
            emailGeneratedRef.current = false;
            setEmailGenerated(false);

            generatedCredentialsRef.current.email =
                "";
        }

        /*
         * If the administrator manually changes the
         * password, that password becomes authoritative.
         */
        if (
            Object.prototype.hasOwnProperty.call(
                changedValues,
                "password"
            )
        ) {
            passwordGeneratedRef.current = false;
            setPasswordGenerated(false);

            generatedCredentialsRef.current.password =
                "";
        }
    };

    const fetchGeneratedCredentials = async (
        username,
        requestId
    ) => {
        try {
            const response = await api.get(
                "/api/users/username-email-suggestion/",
                {
                    params: {
                        username: username.trim(),
                    },
                }
            );

            if (
                requestId !==
                requestIdRef.current
            ) {
                return;
            }

            const data = response?.data || {};

            if (!data.success) {
                console.error(
                    "Credential generation failed:",
                    data
                );

                return;
            }

            const generatedEmail = String(
                data.email || ""
            ).trim();

            const generatedPassword = String(
                data.password || ""
            ).trim();

            /*
             * Save the generated values.
             */
            generatedCredentialsRef.current = {
                email: generatedEmail,
                password: generatedPassword,
            };

            if (generatedEmail) {
                form.setFieldValue(
                    "email",
                    generatedEmail
                );

                emailGeneratedRef.current = true;
                setEmailGenerated(true);
            }

            if (generatedPassword) {
                form.setFieldValue(
                    "password",
                    generatedPassword
                );

                form.setFieldValue(
                    "confirm_password",
                    generatedPassword
                );

                passwordGeneratedRef.current = true;
                setPasswordGenerated(true);
            }
        } catch (error) {
            if (
                requestId !==
                requestIdRef.current
            ) {
                return;
            }

            console.error(
                "Credential suggestion error:",
                error?.response?.data || error
            );
        } finally {
            if (
                requestId ===
                requestIdRef.current
            ) {
                setCheckingCredentials(false);
            }
        }
    };

    const handleUsernameChange = (event) => {
        const username =
            event.target.value || "";

        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        const trimmedUsername =
            username.trim();

        if (!trimmedUsername) {
            requestIdRef.current += 1;

            setCheckingCredentials(false);

            clearGeneratedCredentials();

            return;
        }

        /*
         * Every new username invalidates the previous
         * credential-generation request.
         */
        const currentRequestId =
            ++requestIdRef.current;

        setCheckingCredentials(true);

        debounceRef.current = setTimeout(() => {
            fetchGeneratedCredentials(
                trimmedUsername,
                currentRequestId
            );
        }, 350);
    };

    const handleImageChange = (info) => {
        const file =
            info.file?.originFileObj ||
            info.file;

        if (!file) {
            return;
        }

        if (imageObjectUrlRef.current) {
            URL.revokeObjectURL(
                imageObjectUrlRef.current
            );
        }

        const preview =
            URL.createObjectURL(file);

        imageObjectUrlRef.current = preview;

        setImageFile(file);
        setImagePreview(preview);
    };

    const handleRemoveImage = () => {
        if (imageObjectUrlRef.current) {
            URL.revokeObjectURL(
                imageObjectUrlRef.current
            );

            imageObjectUrlRef.current = null;
        }

        setImageFile(null);
        setImagePreview(null);
    };

    const copyCredential = async (
        value,
        label
    ) => {
        if (!value) {
            return;
        }

        try {
            await navigator.clipboard.writeText(
                String(value)
            );

            setPopup({
                show: true,
                type: "success",
                message:
                    `${label} copied successfully.`,
            });
        } catch (error) {
            console.error(
                "Copy failed:",
                error
            );

            setPopup({
                show: true,
                type: "error",
                message:
                    `Unable to copy the ${label.toLowerCase()}.`,
            });
        }
    };

    const getBackendErrorMessage = (
        responseData
    ) => {
        if (!responseData) {
            return "Unable to create account. Please check the information and try again.";
        }

        if (responseData.errors) {
            const errors =
                responseData.errors;

            const firstError =
                Object.values(errors)[0];

            if (
                Array.isArray(firstError) &&
                firstError.length
            ) {
                return String(
                    firstError[0]
                );
            }

            if (
                typeof firstError ===
                "string"
            ) {
                return firstError;
            }
        }

        if (
            typeof responseData.message ===
            "string"
        ) {
            return responseData.message;
        }

        if (
            typeof responseData.detail ===
            "string"
        ) {
            return responseData.detail;
        }

        const firstError =
            Object.values(responseData)[0];

        if (
            Array.isArray(firstError) &&
            firstError.length
        ) {
            return String(
                firstError[0]
            );
        }

        if (
            typeof firstError ===
            "string"
        ) {
            return firstError;
        }

        return "Unable to create account. Please check the information and try again.";
    };

    const handleSubmit = async (values) => {
        if (checkingCredentials) {
            setPopup({
                show: true,
                type: "error",
                message:
                    "Please wait for credential generation to finish.",
            });

            return;
        }

        const username =
            String(values.username || "").trim();

        const email =
            String(values.email || "").trim();

        const password =
            String(values.password || "");

        const confirmPassword =
            String(
                values.confirm_password || ""
            );

        if (!username) {
            return;
        }

        if (!password) {
            setPopup({
                show: true,
                type: "error",
                message:
                    "Please enter a password.",
            });

            return;
        }

        if (password.length < 8) {
            setPopup({
                show: true,
                type: "error",
                message:
                    "Password must contain at least 8 characters.",
            });

            return;
        }

        if (password !== confirmPassword) {
            setPopup({
                show: true,
                type: "error",
                message:
                    "Passwords do not match.",
            });

            return;
        }

        setLoading(true);

        setPopup({
            show: false,
            type: "",
            message: "",
        });

        setCreatedCredentials(null);

        /*
         * IMPORTANT:
         *
         * The password submitted here is the exact password
         * currently shown in the form.
         *
         * We do not substitute another password after the
         * registration request.
         */
        const data = new FormData();

        data.append(
            "username",
            username
        );

        data.append(
            "email",
            email
        );

        data.append(
            "password",
            password
        );

        data.append(
            "confirm_password",
            password
        );

        data.append(
            "user_type",
            values.user_type || ""
        );

        data.append(
            "gender",
            values.gender || ""
        );

        /*
         * Send the exact values being submitted.
         *
         * This is intentionally false when the generated
         * password has already been placed into the form.
         *
         * The backend should therefore use the submitted
         * password rather than generating a different one.
         */
        data.append(
            "email_generated",
            "false"
        );

        data.append(
            "password_generated",
            "false"
        );

        if (imageFile) {
            data.append(
                "profile_image",
                imageFile
            );
        }

        try {
            const response =
                await api.post(
                    "/register/",
                    data,
                    {
                        headers: {
                            "Content-Type":
                                "multipart/form-data",
                        },
                    }
                );

            const responseData =
                response?.data || {};

            const credentials =
                responseData.credentials ||
                {};

            const registeredUser =
                responseData.user ||
                {};

            /*
             * Prefer what Django explicitly returns.
             *
             * If Django does not return a password,
             * use the EXACT password that was submitted.
             *
             * We never use a second generated password.
             */
            const actualEmail =
                String(
                    credentials.email ||
                    credentials.login_email ||
                    registeredUser.email ||
                    responseData.email ||
                    email ||
                    ""
                ).trim();

            const returnedPassword =
                credentials.password ||
                credentials.initial_password ||
                credentials.initialPassword ||
                credentials.generated_password ||
                registeredUser.initial_password ||
                registeredUser.initialPassword ||
                "";

            const actualPassword =
                String(
                    returnedPassword ||
                    password
                );

            const createdUser = {
                ...registeredUser,

                id:
                    registeredUser.id ||
                    responseData.id ||
                    responseData.user_id ||
                    "",

                username:
                    registeredUser.username ||
                    responseData.username ||
                    username,

                email:
                    actualEmail,

                password:
                    actualPassword,

                initial_password:
                    credentials.initial_password ||
                    registeredUser.initial_password ||
                    actualPassword,

                initialPassword:
                    credentials.initialPassword ||
                    registeredUser.initialPassword ||
                    actualPassword,

                gender:
                    registeredUser.gender ||
                    responseData.gender ||
                    values.gender ||
                    "",

                user_type:
                    registeredUser.user_type ||
                    responseData.user_type ||
                    values.user_type ||
                    "",

                full_name:
                    registeredUser.full_name ||
                    responseData.full_name ||
                    [
                        registeredUser.first_name,
                        registeredUser.last_name,
                    ]
                        .filter(Boolean)
                        .join(" ") ||
                    username,

                profile_image:
                    registeredUser.profile_image ||
                    registeredUser.profileImage ||
                    responseData.profile_image ||
                    responseData.profileImage ||
                    "",
            };

            const isParent =
                String(
                    values.user_type ||
                    createdUser.user_type ||
                    ""
                )
                    .trim()
                    .toLowerCase() ===
                "parent";

            if (isParent) {
                try {
                    await downloadParentAuthenticationPDF(
                        createdUser,
                        {
                            email:
                                actualEmail,
                            password:
                                actualPassword,
                        }
                    );
                } catch (pdfError) {
                    console.error(
                        "Parent authentication PDF generation failed:",
                        pdfError
                    );

                    setPopup({
                        show: true,
                        type: "error",
                        message:
                            "The parent account was created successfully, but the authentication PDF could not be generated.",
                    });
                }
            }

            /*
             * Display exactly what was submitted.
             *
             * This means the password visible here is never
             * changed after the account has been created.
             */
            setCreatedCredentials({
                email: actualEmail,
                password: actualPassword,
                emailGenerated: false,
                passwordGenerated: false,
            });

            if (!isParent) {
                setPopup({
                    show: true,
                    type: "success",
                    message:
                        responseData.message ||
                        "User registered successfully!",
                });
            } else {
                setPopup({
                    show: true,
                    type: "success",
                    message:
                        "Parent account created successfully. The authentication PDF has been downloaded.",
                });
            }

            form.resetFields();

            handleRemoveImage();

            generatedCredentialsRef.current = {
                email: "",
                password: "",
            };

            emailGeneratedRef.current = false;
            passwordGeneratedRef.current = false;

            setEmailGenerated(false);
            setPasswordGenerated(false);
            setCheckingCredentials(false);
        } catch (error) {
            console.error(
                "Registration error:",
                error?.response?.data ||
                error
            );

            setPopup({
                show: true,
                type: "error",
                message:
                    getBackendErrorMessage(
                        error?.response?.data
                    ),
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <style>
                {`
                    .add-user-wrapper {
                        --primary: #0F5B78;
                        --primary-dark: #0B465D;
                        --page-bg: #F4F7F9;
                        --card: #FFFFFF;
                        --text: #172B3A;
                        --muted: #647482;
                        --border: #DCE4E8;
                        --success: #16805C;

                        width: 100%;
                        min-height: calc(100vh - 110px);
                        padding: 14px 18px;
                        background: var(--page-bg);
                        box-sizing: border-box;
                    }

                    .add-user-wrapper * {
                        box-sizing: border-box;
                    }

                    .add-user-page-header {
                        position: relative;
                        min-height: 74px;
                        margin-bottom: 12px;
                        padding: 14px 20px;
                        border-radius: 14px;
                        overflow: hidden;
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        gap: 16px;
                        background:
                            linear-gradient(
                                135deg,
                                #071014 0%,
                                #0B2632 48%,
                                #0F5B78 100%
                            );
                        box-shadow:
                            0 8px 24px rgba(7,16,20,.12);
                    }

                    .add-user-header-pattern {
                        position: absolute;
                        inset: -60%;
                        pointer-events: none;
                        opacity: .4;
                        background:
                            repeating-linear-gradient(
                                135deg,
                                rgba(39,120,168,.14) 0px,
                                rgba(39,120,168,.14) 2px,
                                transparent 2px,
                                transparent 24px
                            );
                        transform: rotate(-5deg);
                    }

                    .add-user-header-content,
                    .header-security-badge {
                        position: relative;
                        z-index: 2;
                    }

                    .add-user-header-content {
                        display: flex;
                        align-items: center;
                        gap: 13px;
                        min-width: 0;
                    }

                    .add-user-header-icon {
                        width: 46px;
                        height: 46px;
                        flex: 0 0 46px;
                        border-radius: 12px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        color: #fff;
                        font-size: 22px;
                        background: rgba(255,255,255,.12);
                        border: 1px solid rgba(255,255,255,.16);
                    }

                    .add-user-page-title {
                        margin: 0 !important;
                        color: #fff !important;
                        font-size: 24px !important;
                    }

                    .add-user-page-subtitle {
                        display: block;
                        margin-top: 4px;
                        color: rgba(255,255,255,.82) !important;
                        font-size: 14px !important;
                    }

                    .header-security-badge {
                        display: flex;
                        align-items: center;
                        gap: 7px;
                        padding: 8px 12px;
                        border-radius: 999px;
                        color: #D9F5EA;
                        background: rgba(22,128,92,.20);
                        border: 1px solid rgba(110,220,181,.22);
                        font-size: 12px;
                        font-weight: 600;
                        white-space: nowrap;
                    }

                    .profile-card,
                    .form-card,
                    .created-credentials-card {
                        border: 1px solid var(--border) !important;
                        border-radius: 14px !important;
                        background: var(--card) !important;
                        box-shadow:
                            0 5px 18px rgba(23,43,58,.055) !important;
                    }

                    .profile-card {
                        height: 100%;
                        min-height: 438px;
                        overflow: hidden;
                    }

                    .form-card {
                        min-height: 438px;
                        padding: 16px 20px 12px !important;
                        overflow: hidden;
                    }

                    .profile-card .ant-card-body {
                        height: 100%;
                        padding: 15px 17px !important;
                    }

                    .form-card .ant-card-body {
                        padding: 0 !important;
                    }

                    .card-top-accent {
                        position: absolute;
                        top: 0;
                        left: 0;
                        right: 0;
                        height: 3px;
                        background:
                            linear-gradient(
                                90deg,
                                #0F5B78,
                                #2778A8,
                                #16805C
                            );
                    }

                    .profile-card-heading,
                    .form-header {
                        display: flex;
                        align-items: center;
                        gap: 10px;
                    }

                    .profile-card-heading {
                        margin-bottom: 8px;
                    }

                    .section-icon,
                    .form-header-icon,
                    .credential-heading-icon {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        flex: 0 0 auto;
                        border-radius: 10px;
                        color: var(--primary);
                        background: #EAF4F7;
                    }

                    .section-icon {
                        width: 36px;
                        height: 36px;
                    }

                    .section-eyebrow {
                        display: block;
                        color: var(--primary) !important;
                        font-size: 11px !important;
                        letter-spacing: 1.1px;
                        font-weight: 800 !important;
                    }

                    .section-title {
                        margin: 2px 0 0 !important;
                        color: var(--text) !important;
                        font-size: 18px !important;
                    }

                    .profile-upload-area {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        text-align: center;
                    }

                    .avatar-frame {
                        position: relative;
                        width: 126px;
                        height: 126px;
                        margin: 4px auto 7px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }

                    .avatar-ring {
                        position: absolute;
                        inset: 0;
                        border-radius: 50%;
                        border: 2px solid #D7E9EE;
                        box-shadow:
                            0 0 0 5px #F3F8FA,
                            0 8px 20px rgba(15,91,120,.10);
                    }

                    .profile-avatar {
                        width: 110px !important;
                        height: 110px !important;
                        border: 4px solid #fff;
                        box-shadow:
                            0 5px 15px rgba(23,43,58,.13);
                        object-fit: cover;
                    }

                    .avatar-camera-badge {
                        position: absolute;
                        right: 4px;
                        bottom: 4px;
                        width: 29px;
                        height: 29px;
                        border-radius: 50%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        color: #fff;
                        background: var(--primary);
                        border: 3px solid #fff;
                    }

                    .profile-title {
                        margin: 2px 0 3px !important;
                        color: var(--text) !important;
                        font-size: 17px !important;
                    }

                    .profile-description {
                        max-width: 285px;
                        color: var(--muted) !important;
                        font-size: 13px !important;
                    }

                    .profile-actions {
                        margin-top: 8px;
                        justify-content: center;
                    }

                    .upload-image-btn,
                    .remove-image-btn {
                        height: 35px !important;
                        border-radius: 8px !important;
                        font-size: 12px !important;
                    }

                    .photo-tip {
                        display: flex;
                        align-items: center;
                        gap: 5px;
                        margin-top: 8px;
                        color: #6C7C86;
                        font-size: 11px;
                    }

                    .photo-tip .anticon {
                        color: var(--success);
                    }

                    .profile-card .ant-divider {
                        margin: 10px 0 !important;
                    }

                    .profile-info-box {
                        display: flex;
                        align-items: flex-start;
                        gap: 9px;
                        padding: 9px;
                        border-radius: 9px;
                        background: #F5F8F9;
                        border: 1px solid #E5ECEF;
                    }

                    .profile-info-box > .anticon {
                        margin-top: 3px;
                        color: var(--primary);
                    }

                    .profile-info-box div {
                        display: flex;
                        flex-direction: column;
                        gap: 2px;
                    }

                    .form-header {
                        margin-bottom: 9px;
                    }

                    .form-header-icon {
                        width: 40px;
                        height: 40px;
                        font-size: 18px;
                    }

                    .form-main-title {
                        margin: 2px 0 !important;
                        color: var(--text) !important;
                        font-size: 20px !important;
                    }

                    .form-header-description {
                        display: block;
                        color: var(--muted) !important;
                        font-size: 13px !important;
                    }

                    .form-section-label {
                        display: flex;
                        align-items: center;
                        gap: 7px;
                        margin: 6px 0 8px;
                        color: var(--text);
                        font-size: 12px;
                        font-weight: 800;
                        text-transform: uppercase;
                    }

                    .form-section-label > span {
                        width: 4px;
                        height: 15px;
                        border-radius: 4px;
                        background: var(--primary);
                    }

                    .form-card .ant-form-item {
                        margin-bottom: 7px !important;
                    }

                    .form-card .ant-form-item-label {
                        padding-bottom: 3px !important;
                    }

                    .form-card .ant-form-item-label > label {
                        color: #314957 !important;
                        font-size: 13px !important;
                        font-weight: 650 !important;
                    }

                    .professional-input,
                    .professional-select .ant-select-selector {
                        border-radius: 8px !important;
                        border-color: #D6E1E5 !important;
                        background: #FBFCFD !important;
                        box-shadow: none !important;
                    }

                    .professional-input {
                        height: 39px !important;
                        font-size: 13px !important;
                    }

                    .professional-input:hover,
                    .professional-input:focus,
                    .professional-input.ant-input-affix-wrapper-focused {
                        border-color: var(--primary) !important;
                        box-shadow:
                            0 0 0 2px rgba(15,91,120,.07) !important;
                    }

                    .professional-select .ant-select-selector {
                        min-height: 39px !important;
                        height: 39px !important;
                    }

                    .form-card .ant-form-item-extra {
                        min-height: 14px !important;
                        margin-top: 2px !important;
                        color: #71818A !important;
                        font-size: 11px !important;
                    }

                    .generated-tag {
                        display: inline-flex !important;
                        align-items: center;
                        gap: 3px;
                        margin: 0 !important;
                        padding: 1px 7px !important;
                        border: 0 !important;
                        border-radius: 999px !important;
                        color: #176B54 !important;
                        background: #E8F6F0 !important;
                        font-size: 10px !important;
                        font-weight: 700;
                    }

                    .credential-spinner {
                        color: var(--primary);
                        animation: credentialPulse 1s infinite;
                    }

                    @keyframes credentialPulse {
                        0%,100% { opacity: .3; }
                        50% { opacity: 1; }
                    }

                    .credential-preview {
                        margin-top: 2px;
                        border: 1px solid #D8E9EE !important;
                        border-radius: 9px !important;
                        background: #F7FBFC !important;
                    }

                    .credential-preview .ant-card-body {
                        padding: 7px 10px !important;
                    }

                    .credential-preview-header {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        gap: 10px;
                        margin-bottom: 4px;
                    }

                    .credential-heading {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                    }

                    .credential-heading-icon {
                        width: 29px;
                        height: 29px;
                    }

                    .credential-heading > div:last-child {
                        display: flex;
                        flex-direction: column;
                    }

                    .credential-heading .ant-typography {
                        font-size: 12px;
                    }

                    .generating-tag {
                        margin: 0 !important;
                        border: 0 !important;
                        background: #EAF4F7 !important;
                        color: var(--primary) !important;
                        font-size: 10px !important;
                    }

                    .credential-row {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        gap: 8px;
                        min-height: 30px;
                        padding: 4px 7px;
                        margin-top: 4px;
                        border-radius: 7px;
                        background: #fff;
                        border: 1px solid #E1EAED;
                    }

                    .credential-value {
                        display: flex;
                        align-items: center;
                        gap: 7px;
                        min-width: 0;
                    }

                    .credential-label {
                        min-width: 42px;
                        font-size: 10px !important;
                    }

                    .credential-text {
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                        color: var(--text) !important;
                        font-size: 12px !important;
                    }

                    .credential-password {
                        letter-spacing: .4px;
                    }

                    .copy-btn {
                        flex: 0 0 auto;
                        height: 26px !important;
                        padding: 0 8px !important;
                        border-radius: 6px !important;
                        border-color: #D3E1E5 !important;
                        color: var(--primary) !important;
                        font-size: 10px !important;
                    }

                    .submit-form-item {
                        margin: 7px 0 0 !important;
                    }

                    .submit-btn {
                        height: 40px !important;
                        border: 0 !important;
                        border-radius: 8px !important;
                        background:
                            linear-gradient(
                                135deg,
                                #0F5B78,
                                #0B465D
                            ) !important;
                        font-size: 13px !important;
                        font-weight: 700 !important;
                    }

                    .created-credentials-card {
                        margin-top: 12px;
                        padding: 12px !important;
                        border-left: 3px solid var(--success) !important;
                    }

                    .created-credentials-card .ant-card-body {
                        padding: 0 !important;
                    }

                    .created-header {
                        display: flex;
                        align-items: center;
                        gap: 9px;
                        margin-bottom: 8px;
                    }

                    .created-success-icon {
                        width: 32px;
                        height: 32px;
                        border-radius: 9px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        color: #fff;
                        background: var(--success);
                    }

                    .created-title {
                        margin: 0 !important;
                        color: var(--text) !important;
                        font-size: 16px !important;
                    }

                    .created-subtitle {
                        color: var(--muted) !important;
                        font-size: 11px !important;
                    }

                    .saved-credential-card {
                        border: 1px solid #E0E8EB !important;
                        border-radius: 8px !important;
                        background: #FAFCFD !important;
                    }

                    .saved-credential-card .ant-card-body {
                        padding: 8px 10px !important;
                    }

                    .saved-card-label {
                        display: flex;
                        align-items: center;
                        gap: 5px;
                        margin-bottom: 4px;
                        color: var(--muted);
                        font-size: 11px;
                        font-weight: 650;
                    }

                    .saved-card-label .anticon {
                        color: var(--primary);
                    }

                    .saved-credential-row {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        gap: 8px;
                    }

                    .saved-credential-value {
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                        color: var(--text) !important;
                        font-size: 12px !important;
                    }

                    @media (max-width: 991px) {
                        .add-user-wrapper {
                            min-height: auto;
                        }

                        .profile-card,
                        .form-card {
                            min-height: auto;
                        }

                        .profile-card {
                            height: auto;
                        }
                    }

                    @media (max-width: 767px) {
                        .add-user-wrapper {
                            padding: 10px;
                        }

                        .add-user-page-header {
                            padding: 13px;
                            min-height: auto;
                        }

                        .header-security-badge {
                            display: none;
                        }

                        .add-user-page-title {
                            font-size: 21px !important;
                        }

                        .form-card {
                            padding: 14px !important;
                        }
                    }

                    @media (max-width: 480px) {
                        .add-user-wrapper {
                            padding: 7px;
                        }

                        .add-user-page-title {
                            font-size: 19px !important;
                        }

                        .form-card {
                            padding: 12px !important;
                        }

                        .credential-row {
                            flex-wrap: wrap;
                        }

                        .credential-value {
                            max-width: calc(100% - 55px);
                        }

                        .copy-btn {
                            margin-left: auto;
                        }
                    }
                `}
            </style>

            <div className="add-user-wrapper">

                {popup.show && (
                    <CartoonAlertPopup
                        type={popup.type}
                        message={popup.message}
                        onClose={closePopup}
                    />
                )}

                <div className="add-user-page-header">

                    <div className="add-user-header-pattern" />

                    <div className="add-user-header-content">

                        <div className="add-user-header-icon">
                            <UserAddOutlined />
                        </div>

                        <div>
                            <Title
                                level={2}
                                className="add-user-page-title"
                            >
                                Add New User
                            </Title>

                            <Text className="add-user-page-subtitle">
                                Create and configure a new account
                                for your school community.
                            </Text>
                        </div>

                    </div>

                    <div className="header-security-badge">
                        <SafetyCertificateOutlined />
                        <span>
                            Secure Account Registration
                        </span>
                    </div>

                </div>

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    onValuesChange={
                        handleFormValuesChange
                    }
                    scrollToFirstError
                    requiredMark="optional"
                >

                    <Row
                        gutter={[18, 18]}
                        align="stretch"
                    >

                        <Col
                            xs={24}
                            lg={8}
                            xl={7}
                        >

                            <Card
                                className="profile-card"
                                bordered={false}
                            >

                                <div className="card-top-accent" />

                                <div className="profile-card-heading">

                                    <div className="section-icon">
                                        <CameraOutlined />
                                    </div>

                                    <div>
                                        <Text className="section-eyebrow">
                                            USER PROFILE
                                        </Text>

                                        <Title
                                            level={4}
                                            className="section-title"
                                        >
                                            Profile Photo
                                        </Title>
                                    </div>

                                </div>

                                <div className="profile-upload-area">

                                    <div className="avatar-frame">

                                        <div className="avatar-ring" />

                                        <Avatar
                                            size={170}
                                            src={
                                                imagePreview ||
                                                defaultAvatar
                                            }
                                            icon={
                                                !imagePreview
                                                    ? (
                                                        <UserOutlined />
                                                    )
                                                    : undefined
                                            }
                                            className="profile-avatar"
                                        />

                                        <div className="avatar-camera-badge">
                                            <CameraOutlined />
                                        </div>

                                    </div>

                                    <Title
                                        level={4}
                                        className="profile-title"
                                    >
                                        Profile Photo
                                    </Title>

                                    <Text className="profile-description">
                                        Upload a clear photo that
                                        helps identify the account
                                        holder.
                                    </Text>

                                    <Space
                                        wrap
                                        className="profile-actions"
                                    >

                                        <Upload
                                            beforeUpload={() =>
                                                false
                                            }
                                            showUploadList={false}
                                            onChange={
                                                handleImageChange
                                            }
                                            accept="image/png,image/jpeg,image/jpg"
                                        >

                                            <Button
                                                icon={
                                                    <UploadOutlined />
                                                }
                                                className="upload-image-btn"
                                            >
                                                Upload Image
                                            </Button>

                                        </Upload>

                                        {imagePreview && (
                                            <Button
                                                danger
                                                onClick={
                                                    handleRemoveImage
                                                }
                                                className="remove-image-btn"
                                            >
                                                Remove
                                            </Button>
                                        )}

                                    </Space>

                                    <div className="photo-tip">
                                        <CheckCircleFilled />
                                        <span>
                                            JPG or PNG • Clear
                                            portrait recommended
                                        </span>
                                    </div>

                                </div>

                                <Divider />

                                <div className="profile-info-box">

                                    <IdcardOutlined />

                                    <div>

                                        <Text strong>
                                            Account Identity
                                        </Text>

                                        <Text>
                                            The username and profile
                                            information identify this
                                            user throughout the system.
                                        </Text>

                                    </div>

                                </div>

                            </Card>

                        </Col>

                        <Col
                            xs={24}
                            lg={16}
                            xl={17}
                        >

                            <Card
                                className="form-card"
                                bordered={false}
                            >

                                <div className="form-header">

                                    <div className="form-header-icon">
                                        <TeamOutlined />
                                    </div>

                                    <div>

                                        <Text className="section-eyebrow">
                                            ACCOUNT INFORMATION
                                        </Text>

                                        <Title
                                            level={3}
                                            className="form-main-title"
                                        >
                                            Create User Account
                                        </Title>

                                        <Text className="form-header-description">
                                            Enter the user's basic
                                            information below.
                                        </Text>

                                    </div>

                                </div>

                                <div className="form-section-label">
                                    <span />
                                    Personal & Login Details
                                </div>

                                <Row gutter={[18, 0]}>

                                    <Col
                                        xs={24}
                                        md={12}
                                    >

                                        <Form.Item
                                            label="Username"
                                            name="username"
                                            rules={[
                                                {
                                                    required: true,
                                                    whitespace: true,
                                                    message:
                                                        "Username is required",
                                                },
                                                {
                                                    min: 2,
                                                    message:
                                                        "Username must contain at least 2 characters",
                                                },
                                            ]}
                                        >

                                            <Input
                                                size="large"
                                                prefix={
                                                    <UserOutlined />
                                                }
                                                placeholder="e.g. Chris B. Sati"
                                                onChange={
                                                    handleUsernameChange
                                                }
                                                autoComplete="off"
                                                className="professional-input"
                                            />

                                        </Form.Item>

                                    </Col>

                                    <Col
                                        xs={24}
                                        md={12}
                                    >

                                        <Form.Item
                                            label={
                                                <Space size={7}>
                                                    <span>
                                                        Email Address
                                                    </span>

                                                    {emailGenerated && (
                                                        <Tag className="generated-tag">
                                                            <CheckCircleFilled />
                                                            Generated
                                                        </Tag>
                                                    )}
                                                </Space>
                                            }
                                            name="email"
                                            rules={[
                                                {
                                                    type: "email",
                                                    message:
                                                        "Enter a valid email address",
                                                },
                                            ]}
                                        >

                                            <Input
                                                size="large"
                                                prefix={
                                                    <MailOutlined />
                                                }
                                                placeholder="Email address"
                                                autoComplete="off"
                                                className="professional-input"
                                                suffix={
                                                    checkingCredentials
                                                        ? (
                                                            <span className="credential-spinner">
                                                                ●
                                                            </span>
                                                        )
                                                        : null
                                                }
                                            />

                                        </Form.Item>

                                    </Col>

                                    <Col
                                        xs={24}
                                        md={12}
                                    >

                                        <Form.Item
                                            label={
                                                <Space size={7}>
                                                    <span>
                                                        Password
                                                    </span>

                                                    {passwordGenerated && (
                                                        <Tag className="generated-tag">
                                                            <CheckCircleFilled />
                                                            Generated
                                                        </Tag>
                                                    )}
                                                </Space>
                                            }
                                            name="password"
                                            rules={[
                                                {
                                                    required: true,
                                                    message:
                                                        "Password is required",
                                                },
                                                {
                                                    min: 8,
                                                    message:
                                                        "Password must contain at least 8 characters",
                                                },
                                            ]}
                                        >

                                            <Input.Password
                                                size="large"
                                                prefix={
                                                    <LockOutlined />
                                                }
                                                placeholder="Password"
                                                iconRender={
                                                    visible =>
                                                        visible
                                                            ? (
                                                                <EyeOutlined />
                                                            )
                                                            : (
                                                                <EyeInvisibleOutlined />
                                                            )
                                                }
                                                autoComplete="new-password"
                                                className="professional-input"
                                            />

                                        </Form.Item>

                                    </Col>

                                    <Col
                                        xs={24}
                                        md={12}
                                    >

                                        <Form.Item
                                            label="Confirm Password"
                                            name="confirm_password"
                                            dependencies={[
                                                "password",
                                            ]}
                                            rules={[
                                                {
                                                    required: true,
                                                    message:
                                                        "Please confirm the password",
                                                },
                                                ({
                                                    getFieldValue,
                                                }) => ({
                                                    validator(
                                                        _,
                                                        value
                                                    ) {
                                                        if (
                                                            !value ||
                                                            getFieldValue(
                                                                "password"
                                                            ) === value
                                                        ) {
                                                            return Promise.resolve();
                                                        }

                                                        return Promise.reject(
                                                            new Error(
                                                                "Passwords do not match"
                                                            )
                                                        );
                                                    },
                                                }),
                                            ]}
                                        >

                                            <Input.Password
                                                size="large"
                                                prefix={
                                                    <LockOutlined />
                                                }
                                                placeholder="Confirm password"
                                                iconRender={
                                                    visible =>
                                                        visible
                                                            ? (
                                                                <EyeOutlined />
                                                            )
                                                            : (
                                                                <EyeInvisibleOutlined />
                                                            )
                                                }
                                                autoComplete="new-password"
                                                className="professional-input"
                                            />

                                        </Form.Item>

                                    </Col>

                                </Row>

                                <div className="form-section-label">
                                    <span />
                                    Role & Classification
                                </div>

                                <Row gutter={[18, 0]}>

                                    <Col
                                        xs={24}
                                        md={12}
                                    >

                                        <Form.Item
                                            label="User Role"
                                            name="user_type"
                                            rules={[
                                                {
                                                    required: true,
                                                    message:
                                                        "Please select user role",
                                                },
                                            ]}
                                        >

                                            <Select
                                                size="large"
                                                placeholder="Select user role"
                                                showSearch
                                                optionFilterProp="children"
                                                className="professional-select"
                                            >

                                                <Option value="proprietor">
                                                    Proprietor
                                                </Option>

                                                <Option value="teacher">
                                                    Teacher
                                                </Option>

                                                <Option value="student">
                                                    Student
                                                </Option>

                                                <Option value="registry">
                                                    Registry
                                                </Option>

                                                <Option value="business_manager">
                                                    Business Manager
                                                </Option>

                                                <Option value="admission officer">
                                                    Admission Officer
                                                </Option>

                                                <Option value="it personel">
                                                    IT Personnel
                                                </Option>

                                            </Select>

                                        </Form.Item>

                                    </Col>

                                    <Col
                                        xs={24}
                                        md={12}
                                    >

                                        <Form.Item
                                            label="Gender"
                                            name="gender"
                                            rules={[
                                                {
                                                    required: true,
                                                    message:
                                                        "Please select gender",
                                                },
                                            ]}
                                        >

                                            <Select
                                                size="large"
                                                placeholder="Select gender"
                                                className="professional-select"
                                            >

                                                <Option value="Male">
                                                    Male
                                                </Option>

                                                <Option value="Female">
                                                    Female
                                                </Option>

                                                <Option value="Other">
                                                    Other
                                                </Option>

                                            </Select>

                                        </Form.Item>

                                    </Col>

                                </Row>

                                {(watchedUsername ||
                                    watchedEmail ||
                                    watchedPassword) && (

                                    <Card
                                        size="small"
                                        className="credential-preview"
                                    >

                                        <div className="credential-preview-header">

                                            <div className="credential-heading">

                                                <div className="credential-heading-icon">
                                                    <KeyOutlined />
                                                </div>

                                                <div>

                                                    <Text strong>
                                                        Login Credentials
                                                    </Text>

                                                    <Text type="secondary">
                                                        These are the credentials
                                                        being submitted.
                                                    </Text>

                                                </div>

                                            </div>

                                            {checkingCredentials && (
                                                <Tag className="generating-tag">
                                                    Generating...
                                                </Tag>
                                            )}

                                        </div>

                                        {watchedEmail && (
                                            <div className="credential-row">

                                                <div className="credential-value">

                                                    <Text
                                                        type="secondary"
                                                        className="credential-label"
                                                    >
                                                        Email
                                                    </Text>

                                                    <Text
                                                        strong
                                                        className="credential-text"
                                                    >
                                                        {watchedEmail}
                                                    </Text>

                                                </div>

                                                <Button
                                                    size="small"
                                                    icon={
                                                        <CopyOutlined />
                                                    }
                                                    onClick={() =>
                                                        copyCredential(
                                                            watchedEmail,
                                                            "Email"
                                                        )
                                                    }
                                                    className="copy-btn"
                                                >
                                                    Copy
                                                </Button>

                                            </div>
                                        )}

                                        {watchedPassword && (
                                            <div className="credential-row">

                                                <div className="credential-value">

                                                    <Text
                                                        type="secondary"
                                                        className="credential-label"
                                                    >
                                                        Password
                                                    </Text>

                                                    <Text
                                                        strong
                                                        className="credential-text credential-password"
                                                    >
                                                        {watchedPassword}
                                                    </Text>

                                                </div>

                                                <Button
                                                    size="small"
                                                    icon={
                                                        <CopyOutlined />
                                                    }
                                                    onClick={() =>
                                                        copyCredential(
                                                            watchedPassword,
                                                            "Password"
                                                        )
                                                    }
                                                    className="copy-btn"
                                                >
                                                    Copy
                                                </Button>

                                            </div>
                                        )}

                                    </Card>
                                )}

                                <Form.Item className="submit-form-item">

                                    <Button
                                        htmlType="submit"
                                        type="primary"
                                        block
                                        size="large"
                                        loading={loading}
                                        icon={
                                            <UserAddOutlined />
                                        }
                                        className="submit-btn"
                                    >
                                        {loading
                                            ? "Creating Account..."
                                            : "Create Account"}
                                    </Button>

                                </Form.Item>

                            </Card>

                        </Col>

                    </Row>

                </Form>

                {createdCredentials && (
                    <Card
                        bordered={false}
                        className="created-credentials-card"
                    >

                        <div className="created-header">

                            <div className="created-success-icon">
                                <CheckCircleFilled />
                            </div>

                            <div>

                                <Title
                                    level={4}
                                    className="created-title"
                                >
                                    Account Created Successfully
                                </Title>

                                <Text className="created-subtitle">
                                    Save these exact login
                                    credentials.
                                </Text>

                            </div>

                        </div>

                        <Row gutter={[16, 16]}>

                            <Col
                                xs={24}
                                md={12}
                            >

                                <Card
                                    size="small"
                                    className="saved-credential-card"
                                >

                                    <div className="saved-card-label">
                                        <MailOutlined />
                                        Login Email
                                    </div>

                                    <div className="saved-credential-row">

                                        <Text
                                            strong
                                            className="saved-credential-value"
                                        >
                                            {
                                                createdCredentials.email
                                            }
                                        </Text>

                                        <Button
                                            size="small"
                                            icon={
                                                <CopyOutlined />
                                            }
                                            onClick={() =>
                                                copyCredential(
                                                    createdCredentials.email,
                                                    "Email"
                                                )
                                            }
                                            className="copy-btn"
                                        >
                                            Copy
                                        </Button>

                                    </div>

                                </Card>

                            </Col>

                            {createdCredentials.password && (
                                <Col
                                    xs={24}
                                    md={12}
                                >

                                    <Card
                                        size="small"
                                        className="saved-credential-card"
                                    >

                                        <div className="saved-card-label">
                                            <LockOutlined />
                                            Login Password
                                        </div>

                                        <div className="saved-credential-row">

                                            <Text
                                                strong
                                                className="saved-credential-value"
                                            >
                                                {
                                                    createdCredentials.password
                                                }
                                            </Text>

                                            <Button
                                                size="small"
                                                icon={
                                                    <CopyOutlined />
                                                }
                                                onClick={() =>
                                                    copyCredential(
                                                        createdCredentials.password,
                                                        "Password"
                                                    )
                                                }
                                                className="copy-btn"
                                            >
                                                Copy
                                            </Button>

                                        </div>

                                    </Card>

                                </Col>
                            )}

                        </Row>

                    </Card>
                )}

            </div>
        </>
    );
};

export default AddStudentsStaff;